import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle, Copy, Home, Gift, Clock, RefreshCw, Loader2, PartyPopper, Phone, Printer, MapPin, User, Truck } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// Simple confetti particle component
const Particle = ({ style }) => (
  <div
    style={{
      position: "fixed",
      width: 10,
      height: 10,
      borderRadius: "50%",
      pointerEvents: "none",
      zIndex: 9999,
      animation: "confettiFall 2.5s ease-in forwards",
      ...style,
    }}
  />
);

export const OrderConfirmationPage = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [showParticles, setShowParticles] = useState(false);
  const [particles, setParticles] = useState([]);
  const hasShownSuccess = useRef(false);

  useEffect(() => {
    fetchOrder();
    // inject confetti animation CSS once
    if (!document.getElementById("confetti-style")) {
      const style = document.createElement("style");
      style.id = "confetti-style";
      style.textContent = `
        @keyframes confettiFall {
          0%   { transform: translateY(-20px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        @keyframes successPop {
          0%   { transform: scale(0.5); opacity: 0; }
          70%  { transform: scale(1.15); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(40px); opacity: 0; }
          to   { transform: translateY(0); opacity: 1; }
        }
        .success-pop { animation: successPop 0.6s cubic-bezier(0.34,1.56,0.64,1) both; }
        .slide-up    { animation: slideUp 0.5s ease both; }
      `;
      document.head.appendChild(style);
    }
  }, [orderId]);

  useEffect(() => {
    if (order?.payment_status === "completed") {
      setTimeout(() => setRevealed(true), 400);
      if (!hasShownSuccess.current) {
        hasShownSuccess.current = true;
        launchConfetti();
        toast.success("🎉 Peman ou a konfime avèk siksè!", { duration: 5000 });
      }
    }
  }, [order]);

  const launchConfetti = () => {
    setShowParticles(true);
    const colors = ["#FF9900", "#22c55e", "#3b82f6", "#f43f5e", "#a855f7", "#FFFFFF", "#FFD700"];
    const newParticles = Array.from({ length: 60 }, (_, i) => ({
      id: i,
      color: colors[i % colors.length],
      left: `${Math.random() * 100}%`,
      top: `-${Math.random() * 20}px`,
      animationDelay: `${Math.random() * 1.5}s`,
      width: `${6 + Math.random() * 10}px`,
      height: `${6 + Math.random() * 10}px`,
      borderRadius: Math.random() > 0.5 ? "50%" : "2px",
    }));
    setParticles(newParticles);
    setTimeout(() => setShowParticles(false), 3500);
  };

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      setOrder(response.data);
      if (response.data.payment_status === "pending") {
        verifyPayment();
      }
    } catch (error) {
      console.error("Error fetching order:", error);
      toast.error("Kòmand pa jwenn");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async () => {
    setVerifying(true);
    try {
      const response = await axios.post(`${API}/moncash/verify-payment?order_id=${orderId}`);
      if (response.data.status === "completed") {
        toast.success("✅ Peman konfime!");
        fetchOrder();
      } else {
        toast.info("Peman an poko konfime. Tanpri eseye ankò.");
      }
    } catch (error) {
      console.error("Error verifying payment:", error);
    } finally {
      setVerifying(false);
    }
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat("fr-HT", { style: "decimal", minimumFractionDigits: 0 }).format(price);

  const copyToClipboard = () => {
    if (!order?.gift_card_code) return;
    const textArea = document.createElement("textarea");
    textArea.value = order.gift_card_code;
    textArea.style.cssText = "position:fixed;left:-9999px;top:-9999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand("copy");
      setCopied(true);
      toast.success("✅ Kòd kopye nan clipboard!");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Pa kapab kopye. Tanpri kopye manyèlman.");
    } finally {
      document.body.removeChild(textArea);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-[#FF9900] mx-auto mb-4" />
            <p className="text-gray-500">Ap chaje kòmand ou...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const isPaid = order?.payment_status === "completed";
  const isMarketplace = order?.type === "marketplace";

  return (
    <div className="min-h-screen flex flex-col bg-gray-50" data-testid="order-confirmation-page">
      {/* Confetti */}
      {showParticles &&
        particles.map((p) => (
          <Particle
            key={p.id}
            style={{
              backgroundColor: p.color,
              left: p.left,
              top: p.top,
              width: p.width,
              height: p.height,
              borderRadius: p.borderRadius,
              animationDelay: p.animationDelay,
            }}
          />
        ))}

      <Header />

      <main className="flex-1 py-8 md:py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {isPaid ? (
            /* ====== SUCCESS STATE ====== */
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden slide-up" data-testid="success-card">

              {/* ✅ Big Success Banner */}
              <div
                className="success-pop p-8 text-center text-white"
                style={{
                  background: "linear-gradient(135deg, #22c55e 0%, #16a34a 50%, #15803d 100%)",
                }}
              >
                <div
                  className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ background: "rgba(255,255,255,0.2)" }}
                >
                  <CheckCircle className="w-14 h-14 text-white drop-shadow-lg" />
                </div>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <PartyPopper className="w-6 h-6 text-yellow-300" />
                  <h1 className="text-3xl font-black tracking-tight">Peman Reyisi!</h1>
                  <PartyPopper className="w-6 h-6 text-yellow-300" />
                </div>
                <p className="text-white/90 text-lg font-semibold mb-1">
                  🎉 Ou peye avèk siksè!
                </p>
                <p className="text-white/75 text-sm">
                  {isMarketplace
                    ? "Mèsi pou acha ou. Men enfòmasyon pou kowòdone livrezon an."
                    : "Mèsi pou acha ou. Men kòd Amazon Gift Card ou a!"}
                </p>

                {/* Small order badge */}
                <div
                  className="inline-block mt-4 px-4 py-1.5 rounded-full text-xs font-bold"
                  style={{ background: "rgba(255,255,255,0.2)", letterSpacing: "0.1em" }}
                >
                  ✓ Kòmand #{order?.id?.slice(0, 8).toUpperCase()}
                </div>
              </div>

              <div className="p-6 md:p-8 space-y-6">

                {/* ====== GIFT CARD CODE or MARKETPLACE INFO ====== */}
                <div
                  className={`rounded-2xl p-6 text-center ${revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
                  style={{
                    background: "linear-gradient(135deg, #1a1f2e 0%, #232F3E 100%)",
                    transition: "opacity 0.6s ease, transform 0.6s ease",
                  }}
                  data-testid="code-reveal-container"
                >
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Gift className="w-5 h-5 text-[#FF9900]" />
                    <span className="text-[#FF9900] font-black uppercase tracking-widest text-xs">
                      {isMarketplace ? "Enfòmasyon Machann" : "Kòd Kat Kado Amazon"}
                    </span>
                  </div>

                  {isMarketplace ? (
                    /* Marketplace seller info */
                    <div className="bg-black/30 rounded-xl p-5 text-left space-y-3">
                      <div className="flex justify-between items-start border-b border-white/10 pb-3">
                        <div>
                          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Pwodwi</p>
                          <p className="text-white font-black text-base leading-tight">
                            {order?.product_title || "—"}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Pri Peye</p>
                          <p className="text-[#FF9900] font-black text-xl">
                            {formatPrice(order?.price_htg)} <span className="text-[10px]">HTG</span>
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Machann</p>
                          <p className="text-white font-bold">{order?.seller_name || "—"}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Telefòn</p>
                          <p className="text-white font-black tracking-widest">
                            {order?.seller_phone || "—"}
                          </p>
                        </div>
                      </div>

                      {/* 🚚 Driver Identity Section */}
                      {order?.delivery_info?.driver_id && (
                        <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-4 text-left">
                          <div className="relative">
                            <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-[#FF9900] bg-gray-800 shadow-lg">
                              {order.delivery_info.driver_photo ? (
                                <img src={order.delivery_info.driver_photo} alt="Driver" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gray-700 text-white font-black text-xs">
                                  {order.delivery_info.driver_name?.[0]}
                                </div>
                              )}
                            </div>
                            <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-1 border-2 border-black">
                               <Truck className="w-2.5 h-2.5 text-white" />
                            </div>
                          </div>
                          
                          <div className="flex-1">
                             <div className="flex items-center gap-2 mb-1">
                               <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Livrezon pa</span>
                               <span className="bg-[#FF9900]/20 text-[#FF9900] text-[8px] font-black px-2 py-0.5 rounded uppercase">{order.delivery_info.status?.replace('_', ' ')}</span>
                             </div>
                             <h4 className="text-white font-black text-base italic leading-tight">
                                {order.delivery_info.driver_name}
                             </h4>
                             <div className="flex items-center gap-3 mt-1.5">
                                <div className="flex items-center gap-1">
                                   <Phone className="w-3 h-3 text-gray-400" />
                                   <span className="text-[11px] font-bold text-white tracking-widest">{order.delivery_info.driver_phone}</span>
                                </div>
                                <span className="w-1.5 h-1.5 bg-white/10 rounded-full" />
                                <div className="flex items-center gap-1">
                                   <p className="text-[10px] font-bold text-gray-300 italic">{order.delivery_info.vehicle_info?.split(' - ')[0]}</p>
                                </div>
                             </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Gift card code */
                    <div className="bg-black/30 rounded-xl p-6 mb-4">
                      <p className="text-[10px] text-gray-400 uppercase tracking-widest mb-3">Kòd ou a:</p>
                      <p
                        className="text-2xl md:text-3xl font-mono text-white tracking-wider break-all"
                        data-testid="gift-card-code"
                        style={{ letterSpacing: "0.15em" }}
                      >
                        {order?.gift_card_code}
                      </p>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="mt-4 flex flex-col gap-3">
                    {!isMarketplace && (
                      <button
                        onClick={copyToClipboard}
                        data-testid="copy-code-button"
                        style={{
                          background: copied ? "#22c55e" : "#FF9900",
                          color: "black",
                          border: "none",
                          borderRadius: "0.75rem",
                          padding: "0.85rem 1.5rem",
                          fontWeight: "900",
                          fontSize: "0.85rem",
                          letterSpacing: "0.05em",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "0.5rem",
                          transition: "background 0.2s",
                        }}
                      >
                        <Copy className="w-4 h-4" />
                        {copied ? "✓ Kòd Kopye!" : "Kopye Kòd la"}
                      </button>
                    )}

                    {isMarketplace && order?.seller_phone && (
                      <a
                        href={`https://wa.me/${order.seller_phone.replace(/\D/g, "")}?text=${encodeURIComponent(
                          `Bonjou ${order.seller_name}, mwen sot peye sou BVGLink pou: ${order.product_title}. Kòmand ID: ${order.id?.slice(0, 8)}`
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 text-white font-black text-xs uppercase tracking-widest py-4 rounded-xl transition-all"
                        style={{ background: "#25D366" }}
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                        </svg>
                        Kontakte Machann via WhatsApp
                      </a>
                    )}

                    {!isMarketplace && (
                      <a
                        href={`https://wa.me/?text=${encodeURIComponent(
                          `🎁 Men kòd kat kado Amazon mwen!\n\n💳 Kòd: ${order?.gift_card_code}\n💰 Valè: $${order?.denomination} USD\n\n📱 Aktive sou: https://www.amazon.com/gc/redeem\n\nBVGLinkAmazon.com`
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 text-white font-bold text-sm py-3 rounded-xl transition-all"
                        style={{ background: "#25D366" }}
                        data-testid="whatsapp-share-button"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                        </svg>
                        Pataje sou WhatsApp
                      </a>
                    )}
                  </div>
                </div>

                {/* Order Details */}
                <div className="border border-gray-100 rounded-xl p-5 space-y-3 bg-gray-50" data-testid="order-details">
                  <h3 className="font-bold text-[#232F3E] text-sm uppercase tracking-wider mb-3">Detay Kòmand</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-gray-400 text-xs uppercase font-bold tracking-wider mb-1">ID Kòmand</p>
                      <p className="font-mono text-[#232F3E] text-xs">{order?.id?.slice(0, 8).toUpperCase()}...</p>
                    </div>
                    {!isMarketplace && (
                      <div>
                        <p className="text-gray-400 text-xs uppercase font-bold tracking-wider mb-1">Valè</p>
                        <p className="font-bold text-[#232F3E]">${order?.denomination} USD</p>
                      </div>
                    )}
                    <div>
                      <p className="text-gray-400 text-xs uppercase font-bold tracking-wider mb-1">Pri Peye</p>
                      <p className="font-bold text-[#FF9900]">{formatPrice(order?.price_htg)} HTG</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs uppercase font-bold tracking-wider mb-1">Tranzaksyon</p>
                      <p className="font-mono text-[#232F3E] text-xs truncate">
                        {order?.moncash_transaction_id || "—"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Instructions for gift card */}
                {!isMarketplace && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <h4 className="font-semibold text-amber-800 mb-2">📖 Kijan pou itilize kòd la:</h4>
                    <ol className="text-sm text-amber-700 space-y-1.5 list-decimal list-inside">
                      <li>Klike bouton anba a pou ale sou Amazon</li>
                      <li>Konekte nan kont Amazon ou (oswa kreye youn)</li>
                      <li>Antre kòd la epi klike "Apply to your balance"</li>
                    </ol>
                    <a
                      href="https://www.amazon.com/gc/redeem"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 flex items-center justify-center gap-2 bg-[#232F3E] hover:bg-[#37475A] text-white font-bold py-3 px-6 rounded-lg transition-colors"
                      data-testid="amazon-redeem-link"
                    >
                      <Gift className="w-5 h-5" />
                      Aktive Kat Kado sou Amazon
                    </a>
                  </div>
                )}

                {/* Print + Home buttons */}
                <div className="flex gap-3">
                  <Button
                    onClick={() => window.print()}
                    variant="outline"
                    className="flex-1 py-5 text-sm font-bold"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Enprime Resi
                  </Button>
                  <Button
                    onClick={() => navigate("/")}
                    variant="outline"
                    className="flex-1 py-5 text-sm font-bold"
                    data-testid="back-home-button"
                  >
                    <Home className="w-4 h-4 mr-2" />
                    Retounen Akèy
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            /* ====== PENDING STATE ====== */
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center slide-up" data-testid="pending-card">
              <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-6">
                <Clock className="w-10 h-10 text-yellow-500" />
              </div>
              <h1 className="text-2xl font-bold text-[#232F3E] mb-2">Ap Tann Konfirmasyon Peman</h1>
              <p className="text-gray-500 mb-6">
                Si ou te fè peman an sou MonCash, klike bouton anba a pou verifye estati a.
              </p>
              <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
                <p className="text-xs text-gray-400 uppercase font-bold tracking-wider mb-1">ID Kòmand</p>
                <p className="font-mono text-[#232F3E] text-sm">{order?.id}</p>
              </div>
              <div className="space-y-3">
                <Button
                  onClick={verifyPayment}
                  disabled={verifying}
                  className="w-full bg-[#FF9900] hover:bg-[#F08C00] text-black font-bold py-6"
                  data-testid="verify-button"
                >
                  {verifying ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Ap verifye...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-5 h-5 mr-2" />
                      Verifye Peman MonCash
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => navigate("/")}
                  variant="outline"
                  className="w-full"
                  data-testid="back-home-pending"
                >
                  <Home className="w-5 h-5 mr-2" />
                  Retounen Akèy
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};
